import { motion } from 'framer-motion';
import { useRef } from 'react';

const Hero = () => {
  const constraintsRef = useRef(null);

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-gray-900 to-black pt-20">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-purple-600/20 filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/3 right-1/4 w-72 h-72 rounded-full bg-blue-600/20 filter blur-3xl animate-blob"></div>
        <div className="absolute bottom-1/4 left-1/3 w-80 h-80 rounded-full bg-indigo-600/20 filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-20">
        <motion.div
          ref={constraintsRef}
          variants={container}
          initial="hidden"
          animate="show"
          className="text-center max-w-4xl mx-auto"
        >
          <motion.div variants={item} className="mb-6">
            <span className="inline-block px-3 py-1 text-xs font-semibold text-primary bg-primary/10 rounded-full mb-4">
              Die Zukunft der Holografie ist da
            </span>
          </motion.div>

          <motion.h1
            variants={item}
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">
              IHoloTool
            </span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400">
              Quantum Hologramm-Übertragung
            </span>
          </motion.h1>

          <motion.p
            variants={item}
            className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto"
          >
            Erleben Sie die Revolution der holografischen Darstellung mit unserem tragbaren Quanten-Hologramm-Übertrager. 
            Scannen, speichern und projizieren Sie Objekte in atemberaubender Qualität – jederzeit und überall.
          </motion.p>

          <motion.div
            variants={item}
            className="flex flex-col sm:flex-row justify-center gap-4 mb-16"
          >
            <a
              href="#preorder"
              className="px-8 py-4 bg-gradient-to-r from-primary to-purple-600 text-white font-medium rounded-full hover:opacity-90 transition-opacity shadow-lg hover:shadow-primary/30"
            >
              Jetzt vorbestellen
            </a>
            <a
              href="#demo"
              className="px-8 py-4 bg-white/10 text-white font-medium rounded-full hover:bg-white/20 transition-colors border border-white/10 backdrop-blur-md"
            >
              Live-Demo ansehen
            </a>
          </motion.div>

          {/* 3D Device Showcase */}
          <motion.div
            variants={item}
            className="relative w-full max-w-2xl mx-auto mt-16"
          >
            <div className="relative z-10 w-full h-64 md:h-96 bg-gradient-to-b from-white/5 to-transparent rounded-3xl border border-white/10 backdrop-blur-lg flex items-center justify-center">
              <div className="text-center p-8">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary/20 to-transparent border border-primary/30 flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Holografische Vorschau
                </h3>
                <p className="text-sm text-gray-400">
                  IHoloTool in Aktion – sehen Sie sich das an
                </p>
              </div>
            </div>
            
            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/30 to-purple-600/30 rounded-3xl blur-3xl opacity-70 -z-10 animate-pulse"></div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="flex flex-col items-center">
          <span className="text-sm text-gray-400 mb-2">Scrollen Sie nach unten</span>
          <div className="w-6 h-10 border-2 border-gray-500 rounded-full flex justify-center p-1">
            <div className="w-1 h-2 bg-gray-400 rounded-full animate-bounce"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
